import classNames from "classnames";
import { useInteractionStates } from "../../../hooks/useInteractionStates";
import { LinkedInputDisplayProps } from "../../BuilderInterface";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../../helpers";
import { LinkedInputDisplayNode } from "../../../build/types";
import "./builder-linked-input-display.scss";

const BuilderLinkedInputDisplay: React.FC<LinkedInputDisplayProps> = ({
    node,
    builderProps,
    formatCurrency,
    formatAmount,
    price,
    quantity
}: LinkedInputDisplayProps) => {
    const { id, attr, stateManager } = node as LinkedInputDisplayNode;
    const { setElement, states } = useInteractionStates<HTMLDivElement>();
    const inputDisplayClass = classNames("builder-linked-input-display-wrapper", {
        [`${attr?.className}`]: !!attr?.className
    });

    const formatC = formatCurrency || ((amount: string | number) => amount.toString());
    const formatA = formatAmount || ((amount: number) => amount.toFixed(0));

    if (builderProps) {
        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        // Calculate styles for builder mode
        const wrapperStyle = getCompoundStyle({
            node,
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
        });
        const priceStyles = getCompoundStyle({
            otherStyle: (node as LinkedInputDisplayNode).priceStyle,
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "priceStyle")
        });

        const quantityStyles = getCompoundStyle({
            otherStyle: (node as LinkedInputDisplayNode).quantityStyle,
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "quantityStyle")
        });

        return (
            <div
                {...attr}
                className={inputDisplayClass}
                style={wrapperStyle}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                onDragStart={e => onDragStart?.(e, id)}
                draggable="true"
                onDragEnd={onDragEnd}
            >
                <span className="builder-linked-input-display-price" style={priceStyles}>
                    {formatC(price)}
                </span>
                <span className="builder-linked-input-display-quantity" style={quantityStyles}>
                    x{formatA(quantity)}
                </span>
            </div>
        );
    } else {
        const { style, ...rest } = attr || {};

        const wrapperStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: node.attr?.style,
            stateManager,
            paths: ["attr_style"]
        });

        const priceStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: (node as LinkedInputDisplayNode).priceStyle,
            stateManager,
            paths: ["priceStyle"]
        });

        const quantityStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: (node as LinkedInputDisplayNode).quantityStyle,
            stateManager,
            paths: ["quantityStyle"]
        });

        return (
            <div ref={setElement} style={wrapperStyle} id={id} {...rest} className={inputDisplayClass}>
                <span className="builder-linked-input-display-price" style={priceStyle}>
                    {formatC(price.toString(), true)}
                </span>
                <span className="builder-linked-input-display-quantity" style={quantityStyle}>
                    x{formatA(quantity)}
                </span>
            </div>
        );
    }
};

export default BuilderLinkedInputDisplay;
